// client/src/components/common/PhoneFilterSheet.tsx
//
// A page's filters on a phone (ST-00207, m4b-contracts §5 "filters collapse into a sheet"): one
// 44 px field-style trigger that says what is shown ("Queue  Needs decision · 3"), opening the
// shared bottom sheet (PickerSheet) with the choices as large radio rows — one group per filter,
// counts on the right, optional section headings. Nothing reaches the page until Apply; Cancel,
// Esc, the backdrop and a drag down leave it as it was; Reset returns the groups to their
// defaults. The trigger reuses the pickers' `.picker-field` look (PickerSheet.css), so a date
// field and a filter field sit side by side as one family. Options: PhoneLayout.css.

import { Fragment, useId, useRef, useState, type ReactNode } from 'react';
import PickerSheet from './PickerSheet';
import './PhoneLayout.css';

export interface PhoneChoice {
  value: string;
  label: ReactNode;
  /** Plain text of the label, for the trigger and screen readers when `label` is not a string. */
  text?: string;
  count?: number | null;
  /** A small mark before the label (a status colour dot). */
  swatch?: ReactNode;
  /** A second line under the label. */
  hint?: ReactNode;
  /** Heading shown above this choice when it starts a new section (e.g. Routes, Customers). */
  section?: string;
}

export interface PhoneChoiceGroup {
  id: string;
  legend: string;
  choices: PhoneChoice[];
  value: string;
  /** Where Reset puts the group. */
  defaultValue?: string;
}

export interface PhoneFilterSheetProps {
  /** Sheet title ("Filter the board"). */
  title: string;
  /** What the trigger filters ("Queue", "Show", "Scope"). */
  label: string;
  /** The current value as the trigger shows it. */
  value: ReactNode;
  /** Plain-text value for the trigger's accessible name (defaults to `value` when a string). */
  valueText?: string;
  /** Filters set away from their defaults: a badge on the trigger. */
  activeCount?: number;
  groups: PhoneChoiceGroup[];
  onApply: (values: Record<string, string>) => void;
  applyLabel?: string;
  /** Leading icon of the trigger. */
  icon?: ReactNode;
}

const choiceText = (c: PhoneChoice): string =>
  c.text ?? (typeof c.label === 'string' || typeof c.label === 'number' ? String(c.label) : c.value);

const FilterIcon = () => (
  <svg className="picker-field__icon" viewBox="0 0 20 20" fill="none" aria-hidden="true">
    <path d="M3 5h14M6 10h8M8.5 15h3" stroke="currentColor" strokeWidth="1.75" strokeLinecap="round" />
  </svg>
);

const ChevronDown = () => (
  <svg className="picker-field__icon" viewBox="0 0 20 20" fill="none" aria-hidden="true">
    <path d="M5.5 8 10 12.5 14.5 8" stroke="currentColor" strokeWidth="1.75" strokeLinecap="round" strokeLinejoin="round" />
  </svg>
);

export default function PhoneFilterSheet({
  title,
  label,
  value,
  valueText,
  activeCount = 0,
  groups,
  onApply,
  applyLabel = 'Apply',
  icon,
}: PhoneFilterSheetProps) {
  const uid = useId();
  const [open, setOpen] = useState(false);
  const [staged, setStaged] = useState<Record<string, string>>({});
  const triggerRef = useRef<HTMLButtonElement | null>(null);
  const firstCheckedRef = useRef<HTMLInputElement | null>(null);

  const current = () => Object.fromEntries(groups.map((g) => [g.id, g.value]));
  const openSheet = () => {
    setStaged(current());
    setOpen(true);
  };
  const apply = () => {
    setOpen(false);
    const next = { ...current(), ...staged };
    if (groups.some((g) => next[g.id] !== g.value)) onApply(next);
  };
  const resettable = groups.filter((g) => g.defaultValue !== undefined);
  const canReset = resettable.some((g) => (staged[g.id] ?? g.value) !== g.defaultValue);
  const reset = () =>
    setStaged((s) => ({ ...s, ...Object.fromEntries(resettable.map((g) => [g.id, g.defaultValue as string])) }));

  const shownText = valueText ?? (typeof value === 'string' ? value : '');
  const firstGroupId = groups[0]?.id;

  return (
    <>
      <div className="picker-field phone-filter-trigger">
        <button
          ref={triggerRef}
          type="button"
          className="picker-field__main"
          onClick={openSheet}
          aria-haspopup="dialog"
          aria-expanded={open}
          aria-label={`${label}: ${shownText}${activeCount ? ` (${activeCount} filter${activeCount === 1 ? '' : 's'} on)` : ''}. Change`}
        >
          {icon ?? <FilterIcon />}
          <span className="phone-filter-trigger__label">{label}</span>
          <span className="picker-field__text">{value}</span>
          {activeCount > 0 && (
            <span className="phone-filter-trigger__badge" aria-hidden="true">
              {activeCount}
            </span>
          )}
          <ChevronDown />
        </button>
      </div>
      <PickerSheet
        open={open}
        title={title}
        onCancel={() => setOpen(false)}
        onApply={apply}
        applyLabel={applyLabel}
        returnFocusRef={triggerRef}
        initialFocusRef={firstCheckedRef}
        headerAction={
          resettable.length > 0 ? (
            <button type="button" className="picker-sheet__text-action phone-filter-reset" onClick={reset} disabled={!canReset}>
              Reset
            </button>
          ) : undefined
        }
      >
        {groups.map((g) => {
          let section: string | undefined;
          return (
            <fieldset key={g.id} className="phone-choices">
              <legend className="phone-choices__legend">
                {g.legend}
              </legend>
              <div className="phone-choices__list">
                {g.choices.map((c) => {
                  const heading = c.section && c.section !== section ? c.section : null;
                  section = c.section ?? section;
                  const checked = (staged[g.id] ?? g.value) === c.value;
                  return (
                    <Fragment key={c.value}>
                      {heading && (
                        <p className="phone-choices__section" aria-hidden="true">
                          {heading}
                        </p>
                      )}
                      <label className="phone-choice">
                        <input
                          ref={g.id === firstGroupId && checked ? firstCheckedRef : undefined}
                          type="radio"
                          name={`${uid}-${g.id}`}
                          value={c.value}
                          checked={checked}
                          onChange={() => setStaged((s) => ({ ...s, [g.id]: c.value }))}
                          aria-label={`${c.section ? `${c.section}: ` : ''}${choiceText(c)}${c.count != null ? `, ${c.count}` : ''}`}
                        />
                        <span className="phone-choice__text">
                          {c.swatch}
                          <span>
                            {c.label}
                            {c.hint && <span className="phone-choice__hint">{c.hint}</span>}
                          </span>
                        </span>
                        {c.count != null && <span className="phone-choice__count">{c.count}</span>}
                      </label>
                    </Fragment>
                  );
                })}
              </div>
            </fieldset>
          );
        })}
      </PickerSheet>
    </>
  );
}
